import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    TrendingUp,
    Repeat,
    Sparkles,
    Compass,
    ArrowRight,
    Layers,
    BadgeEuro,
    LineChart,
} from "lucide-react";

function LeverCard({ index, icon: Icon, title, desc }) {
    return (
        <motion.article
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: index * 0.08 }}
            className="rounded-2xl border border-brand-line bg-white p-8 shadow-premium transition-all hover:-translate-y-1 hover:shadow-lg"
            data-testid={`modele-eco-lever-${index}`}
        >
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange">
                <Icon className="h-6 w-6" />
            </div>
            <h3 className="mt-5 font-display text-xl text-brand-ink sm:text-2xl">{title}</h3>
            <p className="mt-4 text-sm leading-relaxed text-brand-ink/75">{desc}</p>
        </motion.article>
    );
}

export default function ModeleEconomique() {
    useEffect(() => {
        document.title = "Modèle économique · Cursus Physio";
        const meta = document.querySelector('meta[name="description"]') || (() => {
            const m = document.createElement("meta");
            m.name = "description";
            document.head.appendChild(m);
            return m;
        })();
        meta.content =
            "Construire la rentabilité de son cabinet sur des bases solides : leviers économiques de la Prévention Active pour les kinésithérapeutes.";
    }, []);

    return (
        <main className="bg-white" data-testid="page-modele-economique">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-brand-line bg-[#33333A] px-6 py-24 text-white sm:py-32">
                <div aria-hidden className="pointer-events-none absolute -top-40 right-1/4 h-[520px] w-[520px] rounded-full bg-brand-orange/15 blur-[140px]" />
                <div className="relative mx-auto max-w-5xl">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-orange/40 bg-brand-orange/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                        <BadgeEuro className="h-3 w-3" /> Profits
                    </span>
                    <h1 className="mt-6 max-w-4xl font-display text-5xl leading-[0.95] sm:text-6xl lg:text-7xl">
                        Construire la <em className="italic text-brand-orange">rentabilité</em> de son cabinet sur des bases solides.
                    </h1>
                    <p className="mt-7 max-w-2xl text-base text-white/75 sm:text-lg">
                        L'expertise en Prévention Active permet de transformer son cabinet en un
                        modèle économique durable : revenus récurrents, valorisation du temps
                        clinique, et nouveaux flux issus de l'accompagnement client.
                    </p>
                    <div className="mt-9 flex flex-wrap gap-3">
                        <Link
                            to="/expertise-pa"
                            data-testid="modele-eco-cta-cursus"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#d9772c]"
                        >
                            <Compass className="h-4 w-4" /> Voir les 3 cursus
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="modele-eco-cta-contact"
                            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3.5 text-sm font-semibold text-white hover:border-white hover:bg-white/5"
                        >
                            Parler à un conseiller <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* LEVIERS */}
            <section className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
                <div className="mb-12 max-w-3xl">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                        Trois leviers
                    </span>
                    <h2 className="mt-4 font-display text-4xl text-brand-ink sm:text-5xl">
                        Les <em className="italic text-brand-smoke">leviers économiques</em> de la Prévention Active.
                    </h2>
                </div>
                <div className="grid gap-6 md:grid-cols-3" data-testid="modele-eco-leviers-grid">
                    <LeverCard
                        index={0}
                        icon={BadgeEuro}
                        title="Honoraires libres"
                        desc="En sortie de convention, les actes de prévention sont facturés à leur juste valeur, en cohérence avec l'expertise mobilisée et le temps clinique investi."
                    />
                    <LeverCard
                        index={1}
                        icon={Repeat}
                        title="Abonnements & accompagnement long terme"
                        desc="Le passage de la patientèle à la clientèle ouvre l'accès à des revenus mensualisés via des programmes d'accompagnement personnalisés et des abonnements de suivi."
                    />
                    <LeverCard
                        index={2}
                        icon={Layers}
                        title="Effets de levier sur l'activité existante"
                        desc="L'expertise en Prévention Active enrichit votre offre conventionnée actuelle : meilleurs résultats cliniques, fidélisation accrue, recommandations spontanées."
                    />
                </div>
            </section>

            {/* RENTABILITÉ CONSTRUITE */}
            <section
                className="border-y border-brand-line bg-[#FAFAFA] px-6 py-24 sm:py-28"
                data-testid="modele-eco-rentabilite"
            >
                <div className="mx-auto grid max-w-6xl items-start gap-12 lg:grid-cols-[1.2fr,1fr]">
                    <div>
                        <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                            Notre approche
                        </span>
                        <h2 className="mt-4 font-display text-4xl text-brand-ink sm:text-5xl">
                            Une rentabilité <em className="italic text-brand-smoke">construite</em>, pas promise.
                        </h2>
                        <p className="mt-6 max-w-xl text-base leading-relaxed text-brand-ink/75 sm:text-lg">
                            Cursus Physio ne promet pas de chiffres miracles. Nous transmettons une
                            méthode et un cadre — clinique, juridique, économique — pour que chaque
                            kinésithérapeute construise un modèle viable, conforme et aligné avec sa
                            vision professionnelle.
                        </p>
                        <Link
                            to="/contact"
                            data-testid="modele-eco-cta-simuler"
                            className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-orange hover:underline"
                        >
                            Simuler avec un conseiller <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                    <div className="rounded-3xl border border-brand-line bg-white p-8 shadow-premium">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
                                <LineChart className="h-5 w-5" />
                            </div>
                            <p className="font-display text-lg text-brand-ink">
                                Trois piliers méthodologiques
                            </p>
                        </div>
                        <ul className="mt-6 space-y-4 text-sm text-brand-ink/80">
                            <li className="flex items-start gap-3">
                                <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" />
                                <span>
                                    <strong className="text-brand-ink">Clinique</strong> — protocoles
                                    validés, évaluation quantitative, suivi longitudinal.
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" />
                                <span>
                                    <strong className="text-brand-ink">Juridique</strong> — cadre du
                                    hors convention, devoir d'information, conformité Qualiopi.
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" />
                                <span>
                                    <strong className="text-brand-ink">Économique</strong> —
                                    structuration de l'offre, pricing, accompagnement à la transition.
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* CTA FINAL */}
            <section className="bg-white px-6 py-20">
                <div className="mx-auto max-w-3xl text-center">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                        La prochaine étape
                    </p>
                    <h3 className="mt-4 font-display text-3xl text-brand-ink sm:text-4xl">
                        Construisez votre modèle, à votre rythme.
                    </h3>
                    <div className="mt-9 flex flex-wrap justify-center gap-3">
                        <Link
                            to="/expertise-pa"
                            data-testid="modele-eco-cta-cursus-bottom"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#d9772c]"
                        >
                            <TrendingUp className="h-4 w-4" /> Découvrir les cursus
                        </Link>
                        <Link
                            to="/diversification-activite"
                            data-testid="modele-eco-cta-diversification"
                            className="inline-flex items-center gap-2 rounded-full border border-brand-ink/15 bg-white px-6 py-3.5 text-sm font-semibold text-brand-ink hover:border-brand-ink"
                        >
                            Explorer la diversification <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
